import React, { useEffect, useState, useCallback } from 'react'
import Header from '../Header/Header';
import Footer from '../Header/Footer';
import Config from '../Header/Config';
import { FaPrint } from 'react-icons/fa';
import { FaDownload } from 'react-icons/fa';
import html2pdf from 'html2pdf.js';

export default function Daily_stock() {
  const [date, setDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [reportData, setReportData] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [showDiv, setShowDiv] = useState(true);

  const apiUrl = Config.apiUrl;

  // Set date to today's date by default
  useEffect(() => {
    const today = new Date();
    setDate(today.toISOString().substr(0, 10)); // Format date as "YYYY-MM-DD"
  }, []);

  const fetchDailyStock = useCallback(async () => {
    // Use selected date if available, otherwise use today's date
    let dateToUse = date;
    if (!dateToUse) {
      const today = new Date();
      dateToUse = today.toISOString().substr(0, 10);
    }

    setLoading(true);
    setProgress(0);
    setErrorMessage(null);
    setReportData(null);
    
    // Simulate progress
    let progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 90) {
          return 90;
        }
        return prev + 10;
      });
    }, 150);

    try {
      const formData = new FormData();
      formData.append("report_date", dateToUse);
      
      console.log("Sending date to API:", dateToUse);
      console.log("API URL:", `${apiUrl}/daily-stock-report`);

      const response = await fetch(`${apiUrl}/daily-stock-report`, {
        method: 'POST',
        body: formData,
      });

      console.log("Response status:", response.status);

      const responseText = await response.text();
      console.log("Response text:", responseText);

      clearInterval(progressInterval);
      setProgress(100);

      if (response.ok) {
        const data = JSON.parse(responseText);
        console.log("Response data:", data);
        if (data.status) {
          setReportData(data.data);
          setErrorMessage(null);
        } else {
          setErrorMessage("No stock report found for selected date. Please select valid date.");
          setReportData(null);
        }
      } else {
        try {
          const errorData = JSON.parse(responseText);
          console.log("Error response:", errorData);
          setErrorMessage("No stock report found for selected date. Please select valid date.");
          setReportData(null);
        } catch (e) {
          console.log("Error parsing response:", e);
          setErrorMessage("No stock report found for selected date. Please select valid date.");
          setReportData(null);
        }
      }
    } catch (error) {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      setProgress(0);
      console.log("Error:", error);
      setErrorMessage("No stock report found for selected date. Please select valid date.");
      setReportData(null);
    } finally {
      setTimeout(() => {
        setLoading(false);
        setProgress(0);
      }, 500);
    }
  }, [date, apiUrl]);

  // Auto-load report when date changes
  useEffect(() => {
    if (date) {
      fetchDailyStock();
    }
  }, [date, fetchDailyStock]);

  // Format date for filename (YYYY-MM-DD format)
  const formatDateForFilename = (dateString) => {
    if (!dateString) return '';
    const dateObj = new Date(dateString);
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handlePrint = () => {
    // Set document title for PDF filename
    const originalTitle = document.title;
    const formattedDate = formatDateForFilename(date);
    document.title = `Mobile City ${formattedDate} Stock Report`;

    setShowDiv(false);
    setTimeout(() => {
      window.print();
      // Restore original title after printing
      setTimeout(() => {
        document.title = originalTitle;
        setShowDiv(true);
      }, 100);
    }, 0);
  };

  const handleDownloadPDF = () => {
    const element = document.getElementById('daily-stock-report');
    if (!element) {
      alert('Report not available for download');
      return;
    }

    const formattedDate = formatDateForFilename(date);
    const filename = `Mobile City ${formattedDate} Stock Report`;

    // Ensure element is visible
    const originalDisplay = element.style.display;
    const originalVisibility = element.style.visibility;
    element.style.display = 'block';
    element.style.visibility = 'visible';

    // Hide buttons before PDF generation
    setShowDiv(false);
    
    // Wait for DOM to update
    setTimeout(() => {
      // Hide buttons and other non-printable elements
      const buttons = element.querySelectorAll('button, .no-print');
      const buttonDisplays = [];
      buttons.forEach((btn, index) => {
        buttonDisplays[index] = btn.style.display;
        btn.style.display = 'none';
      });

      const opt = {
        margin: [8, 8, 8, 8],
        filename: filename,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          logging: true, // Enable logging for debugging
          letterRendering: true,
          allowTaint: false,
          backgroundColor: '#ffffff',
          onclone: (clonedDoc) => {
            // Ensure cloned element is visible
            const clonedElement = clonedDoc.getElementById('daily-stock-report');
            if (clonedElement) {
              clonedElement.style.display = 'block';
              clonedElement.style.visibility = 'visible';
              clonedElement.style.position = 'relative';
            }
          }
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'portrait',
          compress: true
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };

      html2pdf().set(opt).from(element).save().then(() => {
        // Restore original styles
        element.style.display = originalDisplay;
        element.style.visibility = originalVisibility;
        
        // Restore buttons
        buttons.forEach((btn, index) => {
          btn.style.display = buttonDisplays[index] || '';
        });
        setShowDiv(true);
      }).catch((error) => {
        console.error('PDF generation error:', error);
        // Restore original styles on error
        element.style.display = originalDisplay;
        element.style.visibility = originalVisibility;
        
        // Restore buttons on error
        buttons.forEach((btn, index) => {
          btn.style.display = buttonDisplays[index] || '';
        });
        setShowDiv(true);
        alert('Failed to generate PDF. Please try again.');
      });
    }, 300);
  };

  useEffect(() => {
    const handleBeforePrint = () => {
      setShowDiv(false);
    };

    const handleAfterPrint = () => {    
      setShowDiv(true);
    };

    window.addEventListener('beforeprint', handleBeforePrint);
    window.addEventListener('afterprint', handleAfterPrint);

    return () => {
      window.removeEventListener('beforeprint', handleBeforePrint);
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, []);
  return (
    <>
      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          {/* Menu */}
          <Header />
          {/* / Menu */}
          {/* Layout container */}
          <div className="col-6 layout-page">
            {/* Navbar */}
            {/* / Navbar */}
            {/* Content wrapper */}
            <div className="content-wrapper">
              {/* Content */}
              <div className="container-xxl flex-grow-1 container-p-y col-12">
                <h4 className="fw-bold py-3 mb-4">Daily Stock</h4>
                {/* Basic Layout */}
                <div className="card mb-4">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Daily Stock</h5>
                  </div>
                  <div className="card-body">
                    <div className="row align-items-end mb-3">
                      <div className="col-md-3"></div>
                      <div className="form-group col-md-6 mb-0">
                        <label className="form-label" htmlFor="date" style={{ 
                          fontWeight: '600',
                          color: '#495057',
                          marginBottom: '8px',
                          fontSize: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <i className="bx bx-calendar" style={{ fontSize: '18px', color: '#667eea' }}></i>
                          Select Date for Report
                        </label>
                        <div style={{ position: 'relative' }}>
                        <input
                          type="date"
                          className="form-control"
                            id="date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            style={{
                              padding: '12px 15px',
                              fontSize: '15px',
                              border: '2px solid #e0e0e0',
                              borderRadius: '8px',
                              transition: 'all 0.3s ease',
                              backgroundColor: '#ffffff',
                              cursor: 'pointer'
                            }}
                            onFocus={(e) => {
                              e.target.style.borderColor = '#667eea';
                              e.target.style.boxShadow = '0 0 0 0.2rem rgba(102, 126, 234, 0.25)';
                              e.target.style.backgroundColor = '#f8f9ff';
                            }}
                            onBlur={(e) => {
                              e.target.style.borderColor = '#e0e0e0';
                              e.target.style.boxShadow = 'none';
                              e.target.style.backgroundColor = '#ffffff';
                            }}
                            onMouseEnter={(e) => {
                              if (document.activeElement !== e.target) {
                                e.target.style.borderColor = '#667eea';
                                e.target.style.backgroundColor = '#f8f9ff';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (document.activeElement !== e.target) {
                                e.target.style.borderColor = '#e0e0e0';
                                e.target.style.backgroundColor = '#ffffff';
                              }
                            }}
                          />
                        </div>
                      </div>
                      <div className="col-md-3"></div>
                    </div>
                  </div>
                </div>

                {/* Error Message Display */}
                {errorMessage && !loading && (
                  <div className="card mb-4">
                    <div className="card-body">
                      <div style={{ 
                        padding: "20px", 
                        textAlign: "center",
                        backgroundColor: "#fff3cd",
                        border: "1px solid #ffc107",
                        borderRadius: "8px"
                      }}>
                        <div style={{ 
                          fontSize: "18px", 
                          color: "#856404",
                          fontWeight: "500",
                          marginBottom: "10px"
                        }}>
                          <i className="bx bx-info-circle" style={{ marginRight: "8px", fontSize: "20px" }}></i>
                          {errorMessage}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Report Display */}
                {reportData && !loading && (
                  <div className="card mb-4" id="daily-stock-report">
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <h5 className="mb-0">Daily Stock Report</h5>
                      {showDiv && (
                        <div style={{ display: 'flex', gap: '10px' }}>
                          <button className='btn btn-outline-primary' onClick={handleDownloadPDF}>
                            <FaDownload className="icon_color" /> Download PDF
                          </button>
                          <button className='btn btn-outline-danger' onClick={handlePrint}>
                            <FaPrint className="icon_color" /> Print
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="card-body">
                      <style
                        type="text/css"
                        dangerouslySetInnerHTML={{
                          __html: `
                            .btn-outline-danger {
                              transition: all 0.3s ease;
                            }
                            .btn-outline-danger:hover {
                              background-color: #dc3545;
                              color: #ffffff;
                              transform: scale(1.05);
                              box-shadow: 0 4px 8px rgba(220, 53, 69, 0.3);
                            }
                            .btn-outline-danger:hover .icon_color,
                            .btn-outline-danger:hover svg,
                            .btn-outline-danger:hover i {
                              color: #ffffff !important;
                              fill: #ffffff !important;
                            }
                            .btn-outline-danger:active {
                              transform: scale(0.98);
                            }
                            .btn-outline-primary {
                              transition: all 0.3s ease;
                            }
                            .btn-outline-primary:hover {
                              background-color: #007bff;
                              color: #ffffff;
                              transform: scale(1.05);
                              box-shadow: 0 4px 8px rgba(0, 123, 255, 0.3);
                            }
                            .btn-outline-primary:hover .icon_color,
                            .btn-outline-primary:hover svg,
                            .btn-outline-primary:hover i {
                              color: #ffffff !important;
                              fill: #ffffff !important;
                            }
                            .btn-outline-primary:active {
                              transform: scale(0.98);
                            }
                            @media print {
                              body * {
                                visibility: hidden;
                              }
                              #daily-stock-report, #daily-stock-report * {
                                visibility: visible;
                              }
                              #daily-stock-report {
                                position: absolute;
                                left: 0;
                                top: 0;
                                width: 100%;
                                font-size: 12px;
                              }
                              @page {
                                size: A4;
                                margin: 8mm;
                              }
                              .no-print {
                                display: none !important;
                              }
                              .container-xxl > h4,
                              .card-header h5,
                              .card.mb-4:first-of-type {
                                display: none !important;
                                visibility: hidden !important;
                              }
                              .report-header {
                                margin-bottom: 14px !important;
                                padding: 14px 16px !important;
                                border: 1px solid #2c3e50 !important;
                                border-radius: 4px !important;
                                display: flex !important;
                                justify-content: space-between !important;
                                align-items: center !important;
                              }
                              .report-header-left {
                                flex: 1 !important;
                              }
                              .report-header-right {
                                flex: 1 !important;
                                text-align: right !important;
                              }
                              .report-header h2 {
                                margin: 0 0 5px 0 !important;
                                font-size: 22px !important;
                                font-weight: bold !important;
                                color: #000000 !important;
                                line-height: 1.2 !important;
                              }
                              .report-header h4 {
                                margin: 0 !important;
                                font-size: 14px !important;
                                color: #000000 !important;
                                line-height: 1.2 !important;
                              }
                              .report-header .report-date {
                                margin: 0 !important;
                                font-size: 12px !important;
                                color: #000000 !important;
                                line-height: 1.2 !important;
                              }
                              .report-header .report-date strong {
                                color: #000000 !important;
                              }
                              .summary-label {
                                color: #000000 !important;
                              }
                              .summary-value {
                                color: #000000 !important;
                              }
                              .table-section h5 {
                                color: #000000 !important;
                              }
                              .report-table th {
                                color: #000000 !important;
                                background-color: #f5f5f5 !important;
                              }
                              .report-table td {
                                color: #000000 !important;
                              }
                              .summary-section {
                                margin-bottom: 14px !important;
                              }
                              .summary-section .row {
                                display: flex !important;
                                align-items: stretch !important;
                              }
                              .summary-section .row .col-md-6 {
                                display: flex !important;
                              }
                              .summary-card {
                                padding: 12px 14px !important;
                                margin-bottom: 12px !important;
                                font-size: 12px !important;
                                height: 100% !important;
                                width: 100% !important;
                              }
                              .summary-row {
                                padding: 6px 0 !important;
                                font-size: 12px !important;
                                line-height: 1.4 !important;
                              }
                              .table-section {
                                margin-bottom: 14px !important;
                              }
                              .table-section h5 {
                                margin-bottom: 10px !important;
                                font-size: 13px !important;
                                line-height: 1.2 !important;
                              }
                              .report-table {
                                font-size: 11px !important;
                                margin-bottom: 12px !important;
                              }
                              .report-table th,
                              .report-table td {
                                padding: 6px 8px !important;
                                font-size: 11px !important;
                                line-height: 1.4 !important;
                              }
                              .report-table th {
                                font-size: 12px !important;
                                padding: 7px 8px !important;
                              }
                            }
                            .report-header {
                              margin-bottom: 30px;
                              padding: 20px;
                              background-color: #ffffff;
                              border: 2px solid #2c3e50;
                              border-radius: 8px;
                              display: flex;
                              justify-content: space-between;
                              align-items: center;
                            }
                            .report-header-left {
                              flex: 1;
                            }
                            .report-header-right {
                              flex: 1;
                              text-align: right;
                            }
                            .report-header h2 {
                              margin: 0 0 5px 0;
                              font-weight: bold;
                              color: #000000;
                              font-size: 24px;
                            }
                            .report-header h4 {
                              margin: 0;
                              color: #000000;
                              font-size: 16px;
                            }
                            .report-header .report-date {
                              margin: 0;
                              color: #000000;
                              font-size: 14px;
                            }
                            .report-header .report-date strong {
                              color: #000000;
                            }
                            .summary-section {
                              margin-bottom: 30px;
                            }
                            .summary-section .row {
                              display: flex;
                              align-items: stretch;
                            }
                            .summary-section .row .col-md-6 {
                              display: flex;
                            }
                            .summary-card {
                              border: 1px solid #ddd;
                              border-radius: 5px;
                              padding: 15px;
                              margin-bottom: 15px;
                              background-color: #f9f9f9;
                              height: 100%;
                              width: 100%;
                            }
                            .summary-row {
                              display: flex;
                              justify-content: space-between;
                              padding: 8px 0;
                              border-bottom: 1px solid #eee;
                            }
                            .summary-row:last-child {
                              border-bottom: none;
                            }
                            .summary-label {
                              font-weight: 600;
                              color: #000000;
                            }
                            .summary-value {
                              color: #000000;
                            }
                            .table-section {
                              margin-bottom: 30px;
                            }
                            .table-section h5 {
                              margin-bottom: 15px;
                              color: #000000;
                              font-weight: 600;
                            }
                            .report-table {
                              width: 100%;
                              border-collapse: collapse;
                              margin-bottom: 20px;
                            }
                            .report-table th,
                            .report-table td {
                              border: 1px solid #ddd;
                              padding: 10px;
                              text-align: left;
                            }
                            .report-table th {
                              background-color: #f5f5f5;
                              color: #000000;
                              font-weight: 600;
                            }
                            .report-table td {
                              color: #000000;
                            }
                            .report-table tr:nth-child(even) {
                              background-color: #f9f9f9;
                            }
                          `
                        }}
                      />
                      <div className="report-header">
                        <div className="report-header-left">
                          <h2>Mobile City</h2>
                          <h4>Daily Stock Report</h4>
                        </div>
                        <div className="report-header-right">
                          <p className="report-date"><strong>Report Date:</strong> {reportData.report_date}</p>
                        </div>
                      </div>

                      {/* Summary Section */}
                      {(() => {
                        // Get Cash Amount and Online Amount from summary
                        const cashAmount = parseFloat(reportData.summary.cash) || 0;
                        const onlineAmount = parseFloat(reportData.summary.online) || 0;
                        const closingStockValue = parseFloat(reportData.summary.closing_stock_value) || 0;
                        
                        return (
                          <div className="summary-section">
                            <h5 style={{ marginBottom: '15px', fontWeight: '600' }}>Summary</h5>
                            <div className="row">
                              <div className="col-md-6">
                                <div className="summary-card">
                                  <div className="summary-row">
                                    <span className="summary-label">Opening Stock:</span>
                                    <span className="summary-value">{reportData.summary.opening_stock} units</span>
                                  </div>
                                  <div className="summary-row">
                                    <span className="summary-label">Opening Stock Value:</span>
                                    <span className="summary-value">&#8377;{parseFloat(reportData.summary.opening_stock_value).toLocaleString('en-IN', { maximumFractionDigits: 2 }).replace(/\.00$/, '')}</span>
                                  </div>
                                  <div className="summary-row">
                                    <span className="summary-label">Total Purchased:</span>
                                    <span className="summary-value">{reportData.summary.total_purchased} units</span>
                                  </div>
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="summary-card">
                                  <div className="summary-row">
                                    <span className="summary-label">Total Buyback:</span>
                                    <span className="summary-value">{reportData.summary.total_buyback} units</span>
                                  </div>
                                  <div className="summary-row">
                                    <span className="summary-label">Total Sold:</span>
                                    <span className="summary-value">{reportData.summary.total_sold} units</span>
                                  </div>
                                  <div className="summary-row">
                                    <span className="summary-label">Closing Stock:</span>
                                    <span className="summary-value">{reportData.summary.closing_stock} units</span>
                                  </div>
                                  <div className="summary-row">
                                    <span className="summary-label">Closing Value:</span>
                                    <span className="summary-value">&#8377;{closingStockValue.toLocaleString('en-IN', { maximumFractionDigits: 2 }).replace(/\.00$/, '')}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="row" style={{ marginTop: '15px' }}>
                              <div className="col-md-6">
                                <div className="summary-card">
                                  <div className="summary-row">
                                    <span className="summary-label">Cash Amount:</span>
                                    <span className="summary-value" style={{ fontWeight: 'bold' }}>&#8377;{cashAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 }).replace(/\.00$/, '')}</span>
                                  </div>
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="summary-card">
                                  <div className="summary-row">
                                    <span className="summary-label">Online Amount:</span>
                                    <span className="summary-value" style={{ fontWeight: 'bold' }}>&#8377;{onlineAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 }).replace(/\.00$/, '')}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Sales Section */}
                      <div className="table-section">
                        <h5>Sales Details ({reportData.sales.count} items)</h5>
                        {reportData.sales.items && reportData.sales.items.length > 0 ? (
                          <table className="report-table">
                            <thead>
                              <tr>
                                <th>Sr. No.</th>
                                <th>Model Name</th>
                                <th>In</th>
                                <th>Out</th>
                              </tr>
                            </thead>
                            <tbody>
                              {reportData.sales.items.map((item, index) => (
                                <tr key={index}>
                                  <td>{index + 1}</td>
                                  <td>{item.model_name || 'N/A'}</td>
                                  <td>&#8377;{item.purchase_price ? parseFloat(item.purchase_price).toLocaleString('en-IN', { maximumFractionDigits: 2 }).replace(/\.00$/, '') : '0'}</td>
                                  <td>&#8377;{item.sale_price ? parseFloat(item.sale_price).toLocaleString('en-IN', { maximumFractionDigits: 2 }).replace(/\.00$/, '') : '0'}</td>
                                </tr>
                              ))}
                              {(() => {
                                const totalIn = reportData.sales.items.reduce((sum, item) => {
                                  return sum + (parseFloat(item.purchase_price) || 0);
                                }, 0);
                                const openingStockValue = parseFloat(reportData.summary.opening_stock_value) || 0;
                                const inAmount = totalIn - openingStockValue;
                                return (
                                  <>
                                    <tr style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                                      <td colSpan="2" style={{ textAlign: 'right' }}>Total:</td>
                                      <td>&#8377;{totalIn.toLocaleString('en-IN', { maximumFractionDigits: 2 }).replace(/\.00$/, '')}</td>
                                      <td></td>
                                    </tr>
                                    <tr style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', display: 'none' }}>
                                      <td colSpan="2" style={{ textAlign: 'right' }}>Opening Stock Value:</td>
                                      <td>&#8377;{openingStockValue.toLocaleString('en-IN', { maximumFractionDigits: 2 }).replace(/\.00$/, '')}</td>
                                      <td></td>
                                    </tr>
                                    <tr style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', display: 'none' }}>
                                      <td colSpan="2" style={{ textAlign: 'right' }}>In Amount:</td>
                                      <td>&#8377;{inAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 }).replace(/\.00$/, '')}</td>
                                      <td></td>
                                    </tr>
                                  </>
                                );
                              })()}
                            </tbody>
                          </table>
                        ) : (
                          <p>No sales data available</p>
                        )}
                      </div>

                      {/* Buyback Section */}
                      <div className="table-section">
                        <h5>Buyback Details ({reportData.buyback.count} items)</h5>
                        {reportData.buyback.items && reportData.buyback.items.length > 0 ? (
                          <table className="report-table">
                            <thead>
                              <tr>
                                <th>Sr. No.</th>
                                <th>Model Name</th>
                                <th>In</th>
                              </tr>
                            </thead>
                            <tbody>
                              {reportData.buyback.items.map((item, index) => (
                                <tr key={index}>
                                  <td>{index + 1}</td>
                                  <td>{item.model_name || 'N/A'}</td>
                                  <td>&#8377;{item.price ? parseFloat(item.price).toLocaleString('en-IN', { maximumFractionDigits: 2 }).replace(/\.00$/, '') : '0'}</td>
                                </tr>
                              ))}
                              {(() => {
                                // Calculate Buyback Total
                                const buybackTotal = reportData.buyback.items.reduce((sum, item) => {
                                  return sum + (parseFloat(item.price) || 0);
                                }, 0);
                                
                                // Calculate Sales In Amount (same calculation as in Sales Details)
                                const salesTotalIn = reportData.sales.items.reduce((sum, item) => {
                                  return sum + (parseFloat(item.purchase_price) || 0);
                                }, 0);
                                const openingStockValue = parseFloat(reportData.summary.opening_stock_value) || 0;
                                const salesInAmount = salesTotalIn - openingStockValue;
                                
                                // Calculate Buyback In Amount (Buyback Total + Sales In Amount)
                                const buybackInAmount = buybackTotal + salesInAmount;
                                
                                return (
                                  <>
                                    <tr style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                                      <td colSpan="2" style={{ textAlign: 'right' }}>Total:</td>
                                      <td>&#8377;{buybackTotal.toLocaleString('en-IN', { maximumFractionDigits: 2 }).replace(/\.00$/, '')}</td>
                                    </tr>
                                    <tr style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', display: 'none' }}>
                                      <td colSpan="2" style={{ textAlign: 'right' }}>Sales In Amount:</td>
                                      <td>&#8377;{salesInAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 }).replace(/\.00$/, '')}</td>
                                    </tr>
                                    <tr style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', display: 'none' }}>
                                      <td colSpan="2" style={{ textAlign: 'right' }}>Buyback In Amount:</td>
                                      <td>&#8377;{buybackInAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 }).replace(/\.00$/, '')}</td>
                                    </tr>
                                  </>
                                );
                              })()}
                            </tbody>
                          </table>
                        ) : (
                          <p>No buyback data available</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {loading && (
                  <div className="card mb-4">
                    <div className="card-body">
                      <div style={{ padding: "15px" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
                          <span style={{ fontSize: "14px", fontWeight: "500", color: "#495057" }}>Loading Report...</span>
                          <span style={{ fontSize: "13px", fontWeight: "600", color: "#007bff" }}>{progress}%</span>
                        </div>
                        <div 
                          className="progress" 
                          style={{ 
                            height: "8px", 
                            borderRadius: "10px",
                            backgroundColor: "#e9ecef",
                            overflow: "hidden",
                            boxShadow: "inset 0 1px 2px rgba(0,0,0,0.1)"
                          }}
                        >
                          <div 
                            className="progress-bar progress-bar-striped progress-bar-animated" 
                            role="progressbar" 
                            style={{ 
                              width: `${progress}%`,
                              backgroundColor: "#007bff",
                              backgroundImage: "linear-gradient(45deg, rgba(255,255,255,0.15) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.15) 75%, transparent 75%, transparent)",
                              backgroundSize: "1rem 1rem",
                              transition: "width 0.3s ease",
                              borderRadius: "10px"
                            }}
                            aria-valuenow={progress} 
                            aria-valuemin="0" 
                            aria-valuemax="100"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {/* / Content */}
              {/* Footer */}
              <Footer />
              {/* / Footer */}
              <div className="content-backdrop fade" />
            </div>
            {/* Content wrapper */}
          </div>
          {/* / Layout page */}
        </div>
        {/* Overlay */}
        <div className="layout-overlay layout-menu-toggle" />
      </div>
      {/* / Layout wrapper */}
    </>
  )
}
